(() => {
  'use strict';
  if (customElements.get('momo-program-film')) return;
  class ProgramFilm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-film-label]');
      this.status = this.querySelector('[role="status"]');
      if (!this.video || !this.button || !this.label || !this.status) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.viewport = matchMedia('(max-width: 700px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.auto = this.hasAttribute('data-autoplay');
      this.version = 0;
      this.loaded = Boolean(this.video.getAttribute('src'));
      this.wanted = false;
      this.inView = false;
      this.userPaused = false;
      this.autoFailed = false;
      this.resumeFrame = 0;
      if (this.auto) {
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.loop = true;
        this.video.playsInline = true;
      }
      this.syncPoster = () => {
        if (this.loaded) return;
        this.video.poster = this.viewport.matches ? this.dataset.mobilePoster : this.dataset.desktopPoster;
      };
      this.syncPoster();
      this.viewport.addEventListener('change', this.syncPoster, options);
      this.button.addEventListener('click', () => {
        if (this.auto && this.wanted) {
          this.userPaused = true;
          this.stop();
        } else {
          this.userPaused = false;
          this.start('explicit');
        }
      }, options);
      this.video.addEventListener('play', () => {
        if ((this.auto && !this.wanted) || document.hidden || !this.isConnected) {
          this.video.pause();
          return;
        }
        // Native controls on a manual film may resume after a pause.
        if (!this.auto) { this.wanted = true; this.intent = 'explicit'; }
        // Only an explicit play may take playback away from another player.
        if (this.intent === 'explicit') {
          document.querySelectorAll('video').forEach(video => { if (video !== this.video) video.pause(); });
        }
        this.render();
      }, options);
      this.video.addEventListener('playing', () => {
        if (!this.wanted || document.hidden || !this.isConnected || (this.auto && !this.inView)) {
          this.stop();
          return;
        }
        this.setAttribute('data-playing', '');
        this.render();
      }, options);
      this.video.addEventListener('pause', () => {
        if (this.video.paused) {
          this.wanted = false;
          this.removeAttribute('data-playing');
          this.render();
        }
      }, options);
      this.video.addEventListener('ended', () => {
        this.stop();
        if (!this.auto) {
          this.label.textContent = 'watch again';
          this.button.setAttribute('aria-label', 'watch again: ' + this.video.getAttribute('aria-label'));
        }
      }, options);
      this.video.addEventListener('error', () => {
        if (!this.wanted) return;
        this.autoFailed = true;
        this.stop();
        this.status.textContent = this.auto ? 'the film could not load. tap the film to try again.' : 'the film could not load. tap watch to try again.';
      }, options);
      document.addEventListener('play', event => {
        if (event.target !== this.video && event.target.tagName === 'VIDEO' && !event.target.paused) this.stop();
      }, {...options, capture: true});
      document.addEventListener('pause', event => {
        if (!this.auto || event.target.tagName !== 'VIDEO' || this.resumeFrame) return;
        // A large scroll can reveal this film just before the previous player pauses.
        // Wait for pause handlers and visibility observers before trying again.
        this.resumeFrame = requestAnimationFrame(() => {
          this.resumeFrame = 0;
          this.maybeAutoplay();
        });
      }, {...options, capture: true});
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.maybeAutoplay();
      }, options);
      const preferencesChanged = () => {
        if (!this.canAutoplay() && this.intent === 'auto') this.stop();
        else this.maybeAutoplay();
      };
      this.reducedMotion.addEventListener('change', preferencesChanged, options);
      navigator.connection?.addEventListener?.('change', preferencesChanged, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.inView = entry.isIntersecting && entry.intersectionRatio >= .15;
          if (!this.inView) this.stop();
          else this.maybeAutoplay();
        }, {threshold: [0, .15]});
        this.observer.observe(this);
      } else {
        this.checkViewport = () => {
          const box = this.getBoundingClientRect();
          this.inView = box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < innerHeight;
          if (!this.inView) this.stop();
          else this.maybeAutoplay();
        };
        window.addEventListener('scroll', this.checkViewport, {...options, passive: true});
        window.addEventListener('resize', this.checkViewport, options);
        this.checkViewport();
      }
      this.setAttribute('data-film-ready', '');
      this.render();
    }
    canAutoplay() {
      return this.auto && !this.autoFailed && !this.reducedMotion.matches && !navigator.connection?.saveData;
    }
    maybeAutoplay() {
      if (!this.controller || !this.isConnected || !this.canAutoplay() || this.userPaused || !this.inView || document.hidden || this.wanted || document.querySelector('dialog[open]')) return;
      // Background motion yields to a player the visitor is already watching.
      const anotherPlaying = Array.from(document.querySelectorAll('video')).some(video => video !== this.video && !video.paused && !video.ended);
      if (!anotherPlaying) this.start('auto');
    }
    render() {
      if (this.auto) {
        this.button.hidden = false;
        this.label.textContent = this.wanted ? 'pause preview' : 'play preview';
        this.button.setAttribute('aria-label', this.label.textContent + ': ' + this.video.getAttribute('aria-label'));
        this.button.setAttribute('aria-pressed', String(this.wanted));
      } else {
        this.button.hidden = !this.video.paused;
      }
    }
    async start(intent = 'explicit') {
      if (document.hidden || !this.controller || !this.isConnected || this.wanted) return;
      if (intent === 'auto' && (!this.canAutoplay() || !this.inView || this.userPaused || document.querySelector('dialog[open]'))) return;
      if (intent === 'explicit') this.autoFailed = false;
      const attempt = ++this.version;
      this.intent = intent;
      this.wanted = true;
      this.status.textContent = '';
      this.render();
      if (!this.loaded || this.video.error) {
        this.video.src = this.viewport.matches ? this.dataset.mobileSrc : this.dataset.desktopSrc;
        this.video.poster = this.viewport.matches ? this.dataset.mobilePoster : this.dataset.desktopPoster;
        this.loaded = true;
        this.video.load();
      }
      this.video.controls = !this.auto;
      if (this.video.ended) this.video.currentTime = 0;
      try {
        await this.video.play();
        if (attempt !== this.version) {
          // A stale promise must not pause a newer deliberate play attempt.
          if (!this.wanted || !this.controller || !this.isConnected) this.video.pause();
          return;
        }
        if (document.hidden || (this.auto && !this.inView)) this.stop();
      } catch (error) {
        if (attempt !== this.version || !this.controller) return;
        this.autoFailed = true;
        this.stop();
        this.status.textContent = this.auto ? 'tap the film to play.' : 'the film could not start. tap watch to try again.';
      }
    }
    stop() {
      this.version++;
      this.wanted = false;
      this.video.pause();
      this.removeAttribute('data-playing');
      this.render();
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.stop();
      this.controller.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.resumeFrame);
      this.resumeFrame = 0;
      this.controller = null;
      this.removeAttribute('data-film-ready');
    }
  }
  customElements.define('momo-program-film', ProgramFilm);
})();
